import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { releaseKeys, eventReleaseKeys, commonCalsKeys } from "../query-keys";
import type { EventId, ReleaseId } from "@/types/identifiers";

export { releaseKeys, eventReleaseKeys, commonCalsKeys } from "../query-keys";

// --- Global Releases (Admin) ---

export function useReleases() {
  return useQuery({
    queryKey: releaseKeys.list(),
    queryFn: async () => {
      const result = await api.GET("/api/releases");
      return throwIfError(result, "Failed to fetch releases");
    },
  });
}

export function useRelease(releaseId: ReleaseId) {
  return useQuery({
    queryKey: releaseKeys.detail(releaseId),
    queryFn: async () => {
      const result = await api.GET("/api/releases/{release_id}", {
        params: { path: { release_id: releaseId } },
      });
      return throwIfError(result, "Failed to fetch release");
    },
    enabled: !!releaseId,
  });
}

export function useCreateRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["createRelease"],
    mutationFn: async ({
      file,
      name,
    }: {
      file: File;
      name: string;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", name);

      const response = await fetch("/api/releases", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || "Failed to create release");
      }
      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: releaseKeys.all });
    },
  });
}

export function useUpdateRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["updateRelease"],
    mutationFn: async ({
      releaseId,
      name,
    }: {
      releaseId: ReleaseId;
      name?: string;
    }) => {
      const result = await api.PATCH("/api/releases/{release_id}", {
        params: { path: { release_id: releaseId } },
        body: { name },
      });
      return throwIfError(result, "Failed to update release");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: releaseKeys.all });
      qc.invalidateQueries({ queryKey: commonCalsKeys.all });
    },
  });
}

export function useDeleteRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["deleteRelease"],
    mutationFn: async (releaseId: ReleaseId) => {
      const result = await api.DELETE("/api/releases/{release_id}", {
        params: { path: { release_id: releaseId } },
      });
      return throwIfError(result, "Failed to delete release");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: releaseKeys.all });
      qc.invalidateQueries({ queryKey: commonCalsKeys.all });
    },
  });
}

// --- Event Releases ---

export function useEventReleasesWithCommonCals() {
  return useQuery({
    queryKey: eventReleaseKeys.withCommonCals(),
    queryFn: async () => {
      const result = await api.GET("/api/releases/with-common-cals");
      return throwIfError(result, "Failed to fetch event releases with common cals");
    },
  });
}

export function useEventReleases(eventId: EventId) {
  return useQuery({
    queryKey: eventReleaseKeys.byEvent(eventId),
    queryFn: async () => {
      const result = await api.GET("/api/events/{event_id}/releases", {
        params: { path: { event_id: eventId } },
      });
      return throwIfError(result, "Failed to fetch event releases");
    },
    enabled: !!eventId,
  });
}

export function useEventRelease(eventId: EventId, releaseId: ReleaseId) {
  return useQuery({
    queryKey: eventReleaseKeys.detail(eventId, releaseId),
    queryFn: async () => {
      const result = await api.GET(
        "/api/events/{event_id}/releases/{release_id}",
        {
          params: { path: { event_id: eventId, release_id: releaseId } },
        }
      );
      return throwIfError(result, "Failed to fetch event release");
    },
    enabled: !!eventId && !!releaseId,
  });
}

export function useAddReleaseToEvent(eventId: EventId) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["addReleaseToEvent", eventId],
    mutationFn: async ({ releaseId }: { releaseId: ReleaseId }) => {
      const result = await api.POST("/api/events/{event_id}/releases", {
        params: { path: { event_id: eventId } },
        body: { release_id: releaseId },
      });
      return throwIfError(result, "Failed to add release to event");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventReleaseKeys.byEvent(eventId) });
      qc.invalidateQueries({ queryKey: commonCalsKeys.teamStatus(eventId) });
    },
  });
}

export function useUpdateCommonCals(eventId: EventId, releaseId: ReleaseId) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["updateCommonCals", eventId, releaseId],
    mutationFn: async (commonCals: Record<string, unknown>[]) => {
      const result = await api.PUT(
        "/api/events/{event_id}/releases/{release_id}/common-cals",
        {
          params: { path: { event_id: eventId, release_id: releaseId } },
          body: { common_cals: commonCals },
        }
      );
      return throwIfError(result, "Failed to update common cals");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventReleaseKeys.byEvent(eventId) });
      qc.invalidateQueries({
        queryKey: eventReleaseKeys.detail(eventId, releaseId),
      });
      qc.invalidateQueries({ queryKey: commonCalsKeys.teamStatus(eventId) });
    },
  });
}

export function useRemoveReleaseFromEvent(eventId: EventId) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["removeReleaseFromEvent", eventId],
    mutationFn: async (releaseId: ReleaseId) => {
      const result = await api.DELETE(
        "/api/events/{event_id}/releases/{release_id}",
        {
          params: { path: { event_id: eventId, release_id: releaseId } },
        }
      );
      return throwIfError(result, "Failed to remove release from event");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventReleaseKeys.byEvent(eventId) });
      qc.invalidateQueries({ queryKey: commonCalsKeys.teamStatus(eventId) });
    },
  });
}

export function useToggleEventReleaseDisabled(eventId: EventId) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ["toggleEventReleaseDisabled", eventId],
    mutationFn: async ({
      releaseId,
      disabled,
    }: {
      releaseId: ReleaseId;
      disabled: boolean;
    }) => {
      const result = await api.PATCH(
        "/api/events/{event_id}/releases/{release_id}/disabled",
        {
          params: {
            path: { event_id: eventId, release_id: releaseId },
            query: { disabled },
          },
        }
      );
      return throwIfError(result, "Failed to update release status");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventReleaseKeys.byEvent(eventId) });
      qc.invalidateQueries({ queryKey: commonCalsKeys.teamStatus(eventId) });
    },
  });
}
